import React from 'react';
import { Tag, Button } from 'antd';

/**
 * The list-header chip row: single-select status chips with live counts, a divider,
 * then dashed multi-select "attention" chips. Same row on every purchasing list.
 */
export interface Chip {
  key: string;
  label: React.ReactNode;
  count?: number | null;
  /** attention chips only */
  tone?: 'warn' | 'bad' | 'info';
}

export interface FilterChipsProps {
  chips: Chip[];
  value: string | null; // null = All
  onChange: (key: string | null) => void;
  allLabel?: React.ReactNode;
  allCount?: number | null;
  attention?: Chip[];
  attentionValue?: string[];
  onAttentionChange?: (keys: string[]) => void;
  style?: React.CSSProperties;
}

const ATT_COLOR: Record<NonNullable<Chip['tone']>, string> = { warn: '#d48806', bad: '#cf1322', info: '#1e5fb8' };

const Count: React.FC<{ n?: number | null }> = ({ n }) =>
  n === null || n === undefined ? null : (
    <span style={{ opacity: 0.65, marginLeft: 4, fontVariantNumeric: 'tabular-nums' }}>{n.toLocaleString('en-US')}</span>
  );

const FilterChips: React.FC<FilterChipsProps> = ({
  chips, value, onChange, allLabel = 'All', allCount, attention, attentionValue = [], onAttentionChange, style,
}) => (
  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', ...style }}>
    <Tag.CheckableTag checked={value === null} onChange={() => onChange(null)}>
      {allLabel}<Count n={allCount} />
    </Tag.CheckableTag>
    {chips.map((c) => (
      <Tag.CheckableTag key={c.key} checked={value === c.key} onChange={(on) => onChange(on ? c.key : null)}>
        {c.label}<Count n={c.count} />
      </Tag.CheckableTag>
    ))}
    {attention && attention.length > 0 && onAttentionChange ? (
      <>
        <span style={{ width: 1, height: 20, background: '#d9d9d9', margin: '0 4px' }} />
        {attention.map((a) => {
          const on = attentionValue.indexOf(a.key) >= 0;
          const color = ATT_COLOR[a.tone ?? 'warn'];
          return (
            <Button
              key={a.key}
              size="small"
              shape="round"
              type={on ? 'primary' : 'dashed'}
              style={on ? undefined : { color, borderColor: color }}
              onClick={() => onAttentionChange(on ? attentionValue.filter((k) => k !== a.key) : [...attentionValue, a.key])}
            >
              {a.label}<Count n={a.count} />
            </Button>
          );
        })}
      </>
    ) : null}
  </div>
);

export default FilterChips;
